// Catálogo de extras de «Arma la venta»: banner de promos, categorías, buscador y tarjetas con existencia,
// precio con descuento, burbujas de promos propias, Agregar o −/+ y «✕». Todas miden lo mismo (DEC-47).
import { useMemo, useRef, useState, type ReactNode } from 'react';
import { Minus, PackageX, Plus, X } from 'lucide-react';
import { Button, Input, Skeleton } from '@/kit';
import { cn } from '@/lib/cn';
import { SalePromotionPrice } from '@/shared/patterns/SalePromotion';
import { formatMXN } from '@/shared/lib/format/money';
import { catalogDisplayName } from '@/shared/lib/format/itemDisplayName';
import { itemLabelOf } from '../cartLines';
import { isSoldOut } from '../catalogAvailability';
import {
    EXTRA_CATEGORY_FILTERS,
    extraCategoryOf,
    extraOffersOf,
    type ExtraCategory,
} from '../buildSale';
import type { CardSaleBenefits } from '../promotionSlots';
import type { CatalogItem } from '../types';
import { BenefitRow } from './BenefitRow';
import { FinancingBadge } from './FinancingBadge';
import { StockChip } from './StockChip';

const NO_DESCRIPTIONS = new Map<string, string>();
const SKELETONS = 4;
const NO_SALE_BENEFITS: CardSaleBenefits = { bubbles: [], discountedPriceCents: null };

type ExtraAction = 'add' | 'remove' | 'removeAll';

/** Qué acción de qué tarjeta está en vuelo. */
interface PendingExtra {
    sku: string;
    action: ExtraAction;
}

function ExtraCard({
    item,
    count,
    pending,
    chip,
    benefits,
    onAdd,
    onRemove,
    onRemoveAll,
}: {
    item: CatalogItem;
    benefits: CardSaleBenefits;
    count: number;
    /** Acción de esta tarjeta en vuelo o en fila; `null` si no hay. */
    pending: ExtraAction | null;
    chip?: ReactNode;
    onAdd: () => void;
    onRemove: () => void;
    onRemoveAll: () => void;
}) {
    const name = catalogDisplayName(item, NO_DESCRIPTIONS);
    const brand = item.brand.trim();
    const label = itemLabelOf(item);
    const soldOut = isSoldOut(item);
    const busy = pending !== null;
    const description = item.description?.trim();
    const secondary = [description && description !== name ? description : null, item.sku]
        .filter((part): part is string => !!part)
        .join(' · ');
    const discounted = benefits.discountedPriceCents;
    const hasDiscount = discounted !== null && discounted < item.cashPriceCents;
    const priceCents = hasDiscount ? discounted : item.cashPriceCents;

    return (
        <li
            className={cn(
                'flex min-w-0 flex-col gap-1 rounded-lg border bg-card p-3',
                count > 0 ? 'border-primary' : 'border-border',
            )}
        >
            <div className="flex items-start justify-between gap-2">
                <span className="text-[11px] text-muted-foreground">
                    {brand !== '' ? `${brand} · ${label}` : label}
                </span>
                {count > 0 && (
                    <Button
                        size="icon"
                        variant="ghost"
                        className="-mr-1 -mt-1 size-6"
                        disabled={busy}
                        isLoading={pending === 'removeAll'}
                        aria-label={`Quitar todos los ${name}`}
                        onClick={onRemoveAll}
                    >
                        {pending !== 'removeAll' && <X aria-hidden />}
                    </Button>
                )}
            </div>
            <span
                className="line-clamp-2 h-[34px] text-[13px] font-semibold leading-[17px]"
                title={name}
            >
                {name}
            </span>
            <span
                className="truncate font-mono text-[11px] text-muted-foreground"
                title={secondary}
            >
                {secondary}
            </span>
            <span
                data-testid="extra-original-price"
                className="h-4 text-[11px] leading-4 text-muted-foreground tabular-nums line-through"
            >
                {hasDiscount ? formatMXN(item.cashPriceCents / 100) : null}
            </span>
            <span
                data-testid="extra-price"
                className={cn(
                    'text-[13px] font-semibold tabular-nums',
                    hasDiscount && 'text-success',
                )}
            >
                {formatMXN(priceCents / 100)}
            </span>
            <StockChip item={item} className="self-start" />
            {/* Renglón reservado en todas las tarjetas, haya marca o no. */}
            <span data-section="financing" className="flex h-[18px] min-w-0">
                {item.financeable !== undefined && (
                    <FinancingBadge financeable={item.financeable} />
                )}
            </span>
            {/* Sin promos el hueco se queda para no mover el alto. */}
            {chip}
            <span data-section="promos" className="block h-6 min-w-0 overflow-hidden">
                {benefits.bubbles.length > 0 && <BenefitRow bubbles={benefits.bubbles} />}
            </span>
            <div className="mt-auto pt-2">
                {count > 0 ? (
                    <div className="flex items-center justify-between gap-2">
                        <span className="inline-flex items-center gap-2 rounded-md border border-border">
                            <Button
                                size="icon"
                                variant="ghost"
                                disabled={busy}
                                isLoading={pending === 'remove'}
                                aria-label={`Uno menos de ${name}`}
                                onClick={onRemove}
                            >
                                {pending !== 'remove' && <Minus aria-hidden />}
                            </Button>
                            <span
                                data-testid={`qty-${item.sku}`}
                                className="min-w-4 text-center tabular-nums"
                            >
                                {count}
                            </span>
                            <Button
                                size="icon"
                                variant="ghost"
                                disabled={busy || soldOut}
                                isLoading={pending === 'add'}
                                aria-label={`Uno más de ${name}`}
                                onClick={onAdd}
                            >
                                {pending !== 'add' && <Plus aria-hidden />}
                            </Button>
                        </span>
                        <SalePromotionPrice
                            originalCents={item.cashPriceCents * count}
                            netCents={priceCents * count}
                            className={cn(
                                'text-[12px]',
                                hasDiscount ? 'text-success' : 'text-muted-foreground',
                            )}
                        />
                    </div>
                ) : (
                    <Button
                        size="sm"
                        variant="outline"
                        fullWidth
                        disabled={busy || soldOut}
                        isLoading={pending === 'add'}
                        aria-label={`Agregar ${name}`}
                        onClick={onAdd}
                    >
                        {pending === 'add' ? 'Agregando…' : 'Agregar'}
                    </Button>
                )}
            </div>
        </li>
    );
}

export function ExtrasCatalog({
    items,
    counts,
    loading,
    error,
    onRetry,
    onAdd,
    onRemove,
    onRemoveAll,
    chip,
    banner,
    benefits,
}: {
    items: CatalogItem[];
    /** Piezas en la venta por SKU. */
    counts: ReadonlyMap<string, number>;
    loading: boolean;
    error: boolean;
    onRetry: () => void;
    onAdd: (sku: string) => Promise<void>;
    onRemove: (sku: string) => Promise<void>;
    /** «✕»: suelta todas las líneas del SKU. */
    onRemoveAll: (sku: string) => Promise<void>;
    /** Chip de Promociones por tarjeta. */
    chip?: (item: CatalogItem) => ReactNode;
    /** Banner de promos de categoría, con todos los extras del catálogo. */
    banner?: (items: readonly CatalogItem[]) => ReactNode;
    /** Burbujas propias y precio por unidad con descuento que pone Promociones. */
    benefits?: (item: CatalogItem) => CardSaleBenefits;
}) {
    const [category, setCategory] = useState<ExtraCategory>('ALL');
    const [query, setQuery] = useState('');
    const visible = useMemo(() => extraOffersOf(items, category, query), [items, category, query]);
    const allExtras = useMemo(() => extraOffersOf(items, 'ALL', ''), [items]);
    const tabs = useMemo(() => {
        const present = new Set(allExtras.map(extraCategoryOf));
        return EXTRA_CATEGORY_FILTERS.filter((t) => t.value === 'ALL' || present.has(t.value));
    }, [allExtras]);
    const inSale = [...counts.values()].reduce((sum, n) => sum + n, 0);
    const [pending, setPending] = useState<readonly PendingExtra[]>([]);
    // Las escrituras van en fila: cada tarjeta se apaga solo mientras la suya está pendiente.
    const queue = useRef<Promise<unknown>>(Promise.resolve());
    const queued = useRef(new Set<string>());

    const run = (sku: string, action: ExtraAction, fn: (sku: string) => Promise<void>): void => {
        if (queued.current.has(sku)) return;
        queued.current.add(sku);
        const entry: PendingExtra = { sku, action };
        setPending((prev) => [...prev, entry]);
        const done = (): void => {
            queued.current.delete(sku);
            setPending((prev) => prev.filter((p) => p !== entry));
        };
        queue.current = queue.current.then(() => fn(sku)).then(done, done);
    };
    const pendingOf = (sku: string): ExtraAction | null =>
        pending.find((p) => p.sku === sku)?.action ?? null;

    return (
        <section aria-label="Extras" className="flex flex-col gap-3">
            {allExtras.length > 0 && banner?.(allExtras)}
            <div className="flex items-baseline gap-2">
                <h2 className="text-[14px] font-semibold">Extras</h2>
                <span className="text-[12px] text-muted-foreground">
                    opcionales · {inSale} en la venta
                </span>
            </div>
            <div className="flex flex-wrap items-center gap-2">
                {tabs.length > 2 && (
                    <div className="flex flex-wrap gap-1.5" role="group" aria-label="Categoría">
                        {tabs.map((tab) => (
                            <button
                                key={tab.value}
                                type="button"
                                aria-pressed={category === tab.value}
                                onClick={() => {
                                    setCategory(tab.value);
                                }}
                                className={cn(
                                    'rounded-full border px-3 py-1 text-[12px] font-medium transition-colors',
                                    category === tab.value
                                        ? 'border-primary bg-primary text-primary-foreground'
                                        : 'border-border bg-card text-foreground hover:border-primary/50',
                                )}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>
                )}
                <Input
                    type="search"
                    className="min-w-48 flex-1"
                    placeholder="Busca un accesorio, SIM o servicio"
                    aria-label="Buscar extra"
                    value={query}
                    onChange={(e) => {
                        setQuery(e.target.value);
                    }}
                    onKeyDown={(e) => {
                        if (e.key !== 'Enter') return;
                        e.preventDefault();
                        // Enter agrega la primera coincidencia con existencia y limpia para el siguiente.
                        const first = visible.find((i) => !isSoldOut(i));
                        if (first === undefined) return;
                        run(first.sku, 'add', onAdd);
                        setQuery('');
                    }}
                />
            </div>
            {loading ? (
                <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
                    {Array.from({ length: SKELETONS }, (_, i) => (
                        <Skeleton key={i} className="h-56" />
                    ))}
                </div>
            ) : error ? (
                <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed border-border py-8 text-center">
                    <PackageX className="size-7 text-muted-foreground" aria-hidden />
                    <p className="text-[13px] text-muted-foreground">
                        No se pudo leer el catálogo de la tienda.
                    </p>
                    <Button size="sm" variant="secondary" onClick={onRetry}>
                        Volver a intentar
                    </Button>
                </div>
            ) : visible.length === 0 ? (
                <p className="text-[13px] text-muted-foreground">Ningún extra coincide.</p>
            ) : (
                <ul className="grid grid-cols-2 gap-3 md:grid-cols-3">
                    {visible.map((item) => (
                        <ExtraCard
                            key={item.sku}
                            item={item}
                            count={counts.get(item.sku) ?? 0}
                            pending={pendingOf(item.sku)}
                            chip={chip?.(item)}
                            benefits={benefits?.(item) ?? NO_SALE_BENEFITS}
                            onAdd={() => {
                                run(item.sku, 'add', onAdd);
                            }}
                            onRemove={() => {
                                run(item.sku, 'remove', onRemove);
                            }}
                            onRemoveAll={() => {
                                run(item.sku, 'removeAll', onRemoveAll);
                            }}
                        />
                    ))}
                </ul>
            )}
        </section>
    );
}
